import { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  FieldErrors,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { BookOpenIcon, ClipboardCheckIcon } from '@heroicons/react/outline';

import {
  ActivityPdf,
  ActivityType,
  ActivityUseType,
  useEditActivityFileSettingsMutation,
} from '../../../generated/graphql';

import { useAuth } from '../../../contexts/AuthContext';
import {
  defaultActivitySchema,
  FollowUpForm,
  SavedActivityInputPreview,
} from '../../lib/followUp';
import { formatProviderActivityForFollowUp } from '../../lib/providerActivity';
import { customToast } from '../../components/ToastAlert/customToast';
import PillButton from '../../components/PillButton';
import CircleCheck from '../../svgs/CircleCheck';
import AddANewResourceMenu from '../../components/AddANewResourceMenu';
import AddLinkModal from '../../components/Modals/AddLinkModal';
import AddPDFModal from '../../components/Modals/AddPDFModal';
import AddImageModal from '../../components/Modals/AddImageModal';
import AddAudioModal from '../../components/Modals/AddAudioModal';
import AddOrEditTextModal from '../../components/Modals/AddOrEditTextModal';
import AddResourceModal, {
  AddResourceModalRef,
} from '../../components/Modals/AddResourceModal';
import AddStandardAssessmentsModal from '../../components/Modals/AddStandardAssessmentsModal';
import FileSettingsModal from '../../components/Modals/FileSettingsModal';
import SectionLabel from '../../components/SectionLabel';
import ActivityCreationCard from '../FollowUpBuilder/ActivityCreationCard';
import { FILE_ACTIVITY_TYPES } from '../../lib/activity';

type PrependActivity = (activity: SavedActivityInputPreview) => void;

interface Props {
  activities: SavedActivityInputPreview[];
  register: UseFormRegister<FollowUpForm>;
  watch: UseFormWatch<FollowUpForm>;
  setValue: UseFormSetValue<FollowUpForm>;
  errors: FieldErrors<FollowUpForm>;
  remove: UseFieldArrayRemove;
  update: UseFieldArrayUpdate<FollowUpForm, 'activities'>;
  prepend: PrependActivity;
  compactLayout?: boolean;
  attachPosition?: 'top' | 'bottom';
  hideSectionLabel?: boolean;
}

const ScheduleMailResourcesSection: FC<Props> = ({
  activities,
  register,
  watch,
  setValue,
  errors,
  remove,
  update,
  prepend,
  compactLayout = false,
  attachPosition = 'top',
  hideSectionLabel = false,
}) => {
  const { authedProviderUser } = useAuth();
  const addResourceModalRef = useRef<AddResourceModalRef | null>(null);

  const [isAddPDFModalOpen, setIsAddPDFModalOpen] = useState(false);
  const [isAddLinkModalOpen, setIsAddLinkModalOpen] = useState(false);
  const [isAddAudioModalOpen, setIsAddAudioModalOpen] = useState(false);
  const [isAddImageModalOpen, setIsAddImageModalOpen] = useState(false);
  const [isAddOrEditTextModalOpen, setIsAddOrEditTextModalOpen] =
    useState(false);
  const [isAddResourceModalOpen, setIsAddResourceModalOpen] = useState(false);
  const [
    isAddStandardAssessmentsModalOpen,
    setIsAddStandardAssessmentsModalOpen,
  ] = useState(false);
  const [isFileSettingsModalOpen, setIsFileSettingsModalOpen] = useState(false);
  const [selectedActivityPdfId, setSelectedActivityPdfId] = useState<string>();

  const [editActivityFileSettingsMutation] =
    useEditActivityFileSettingsMutation();

  const addActionActivity = () => {
    prepend({
      ...defaultActivitySchema,
      sendFollowUpActivityInputRaw: {
        ...defaultActivitySchema.sendFollowUpActivityInputRaw,
        dateCreated: Date.now(),
      },
    });
  };

  const handleUpdateActivityUseType = async (
    activityId: string,
    activityType: ActivityType,
    activityUseType: ActivityUseType,
    activityIndex: number,
  ) => {
    try {
      const editActivityFileSettingsInput = {
        activityId,
        activityType,
        activityUseType,
        ...(FILE_ACTIVITY_TYPES.includes(activityType) && {
          activityType: ActivityType.Pdf,
        }),
      };

      const { data } = await editActivityFileSettingsMutation({
        variables: { input: editActivityFileSettingsInput },
      });

      const updatedActivity = data?.editActivityFileSettings;

      if (updatedActivity) {
        customToast.success('Successfully changed file settings');
        update(
          activityIndex,
          formatProviderActivityForFollowUp(updatedActivity),
        );
      }
    } catch (err) {
      customToast.error((err as Error).message);
    }
  };

  const requiresProviderSignatureFirst = (
    activity: SavedActivityInputPreview,
  ) =>
    activity?.sendFollowUpActivityInputRaw?.programAssessmentSignatureUsers?.some(
      ({ providerUserId }) => providerUserId === authedProviderUser?.id,
    ) &&
    activity?.multiSignatureAssessmentConfig?.assessmentSignatureUsers?.[0]
      ?.signatureUserType === 'SendingProviderUser';

  const attachSection = (
    <>
      {!hideSectionLabel && <SectionLabel title="Resources" />}
      <div
        className={classNames(
          hideSectionLabel ? 'mb-4' : 'mb-6 mt-4',
          'w-full',
          compactLayout
            ? 'grid grid-cols-2 gap-2'
            : 'flex flex-row items-center justify-between gap-2',
        )}
      >
        <PillButton
          IconComponent={BookOpenIcon}
          title="Library"
          onClick={() => setIsAddResourceModalOpen(true)}
        />
        <PillButton
          IconComponent={ClipboardCheckIcon}
          title="Assessments"
          onClick={() => setIsAddStandardAssessmentsModalOpen(true)}
        />
        <PillButton
          IconComponent={CircleCheck}
          title="Activity"
          onClick={addActionActivity}
        />
        <div className={classNames('w-full', compactLayout && '[&>div]:mx-0')}>
          <AddANewResourceMenu
            onClickAddLink={() => setIsAddLinkModalOpen(true)}
            onClickAddImage={() => setIsAddImageModalOpen(true)}
            onClickAddPDF={() => setIsAddPDFModalOpen(true)}
            onClickAddAudio={() => setIsAddAudioModalOpen(true)}
            onClickAddText={() => setIsAddOrEditTextModalOpen(true)}
          />
        </div>
      </div>
    </>
  );

  const activityCards = activities.map((activity, activityIndex) => (
    <ActivityCreationCard
      activity={activity}
      {...activity.activityPreviewCardProps}
      key={`${activity.sendFollowUpActivityInputRaw.dateCreated}-${activityIndex}`}
      activityIndex={activityIndex}
      register={register}
      watch={watch}
      setValue={setValue}
      errors={errors}
      activitiesLength={activities.length}
      onClickCloseX={() => remove(activityIndex)}
      onClickModifyFileSettings={(activityPdfId: string) => {
        setSelectedActivityPdfId(activityPdfId);
        setIsFileSettingsModalOpen(true);
      }}
      handleUpdateActivityUseType={handleUpdateActivityUseType}
      requiresSendingProviderSignatureFirst={requiresProviderSignatureFirst(
        activity,
      )}
    />
  ));

  return (
    <>
      {attachPosition === 'top' && attachSection}
      {attachPosition === 'top' && activityCards}

      {attachPosition === 'bottom' && (
        <div
          className={classNames(
            activities.length > 0 && 'mt-6 border-t border-neutral-75 pt-6',
          )}
        >
          {attachSection}
          {activityCards}
        </div>
      )}

      <AddLinkModal
        isModalOpen={isAddLinkModalOpen}
        setClosed={() => setIsAddLinkModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddPDFModal
        isModalOpen={isAddPDFModalOpen}
        setClosed={() => setIsAddPDFModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddImageModal
        isModalOpen={isAddImageModalOpen}
        setClosed={() => setIsAddImageModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddAudioModal
        isModalOpen={isAddAudioModalOpen}
        setClosed={() => setIsAddAudioModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddOrEditTextModal
        isModalOpen={isAddOrEditTextModalOpen}
        setClosed={() => setIsAddOrEditTextModalOpen(false)}
        followUpMode
        prependToFollowUp={prepend}
      />
      <AddResourceModal
        isModalOpen={isAddResourceModalOpen}
        setClosed={() => setIsAddResourceModalOpen(false)}
        prependToFollowUp={prepend}
        ref={addResourceModalRef}
      />
      <AddStandardAssessmentsModal
        isModalOpen={isAddStandardAssessmentsModalOpen}
        setClosed={() => setIsAddStandardAssessmentsModalOpen(false)}
        prependToFollowUp={prepend}
      />
      <FileSettingsModal
        followUpMode
        isModalOpen={isFileSettingsModalOpen}
        setClosed={() => setIsFileSettingsModalOpen(false)}
        activityPdfId={selectedActivityPdfId}
        onUpdated={(updatedActivityPdf: ActivityPdf) => {
          const updatedActivityIndex = activities.findIndex(
            (activity) =>
              activity.sendFollowUpActivityInputRaw.activityPDFId ===
              updatedActivityPdf.id,
          );
          if (updatedActivityIndex !== -1) {
            update(
              updatedActivityIndex,
              formatProviderActivityForFollowUp(updatedActivityPdf),
            );
          }
          addResourceModalRef.current?.refetchProviderActivities();
        }}
      />
    </>
  );
};

export default ScheduleMailResourcesSection;
